
import React, { useState, useEffect, useCallback } from "react";
import { User } from "@/entities/User";
import { MonthlyPlan } from "@/entities/MonthlyPlan";
import { FinancialCategory } from "@/entities/FinancialCategory";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/lib/utils";
import { 
  PlusCircle,
  Target,
  Calendar,
  AlertCircle, // Added this import for error icon
  TrendingUp // Changed from BarChart3 to TrendingUp to match navigation menu
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import DashboardStats from "../components/dashboard/DashboardStats";
import MonthSelector from "../components/dashboard/MonthSelector";
import ExpensePieChart from "../components/dashboard/ExpensePieChart";
import { useMonth } from "../components/MonthContext";
import { NetworkErrorHandler, safeEntityCall } from "../components/utils/errorHandler"; // Added these imports
import { PageErrorBoundary, CriticalComponentErrorBoundary } from "../components/utils/ErrorBoundary";
import { useToast } from '@/hooks/useToast';
import { useAccount, useAccountDataReloader } from "@/components/auth/AccountContext";
import RecentTransactions from "../components/dashboard/RecentTransactions";
import { categoryDataService } from "@/services/CategoryDataService";
import { CategoryFilters } from "@/types/CategoryTypes";

interface UserWithAccount extends User {
  current_account_id: string;
}

interface CategoryForProgress {
  id: string;
  name: string;
  planned: number;
  spent: number;
  percentage: number;
  parent_id?: string;
  type?: string;
  planned_amount?: number;
  spent_amount?: number;
  received_amount?: number; // Adicionado para receitas
  children?: CategoryForProgress[];
}

interface DashboardData {
  currentPlan: MonthlyPlan | null;
  categories: FinancialCategory[];
  isLoading: boolean;
  error: string | null;
}

export default function Dashboard(): JSX.Element {
  const { selectedMonth, selectedYear } = useMonth();
  const { currentAccountId } = useAccount();
  const { showError, showWarning } = useToast();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<UserWithAccount | null>(null);
  const [currentPlan, setCurrentPlan] = useState<MonthlyPlan | null>(null);
  const [categories, setCategories] = useState<FinancialCategory[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [incomes, setIncomes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);



  const createNewPlan = useCallback(async (): Promise<void> => {
    if (!user?.current_account_id) {
      showWarning('Selecione ou crie uma conta primeiro!');
      return;
    }
    
    try {
      console.log('🔄 Creating new plan...');
      const newPlan = await safeEntityCall(
        () => MonthlyPlan.create({
          year: selectedYear,
          month: selectedMonth + 1, // Converter 0-11 para 1-12 para o banco
          account_id: currentAccountId
        }),
        'MonthlyPlan',
        'create'
      );
      
      console.log('✅ Plan created:', newPlan);
      setCurrentPlan(newPlan);
      setError(null); // Clear error on success
    } catch (error) {
      console.error('❌ Failed to create plan:', error);
      showError(NetworkErrorHandler.getErrorMessage(error as Error));
    }
  }, [currentAccountId, selectedYear, selectedMonth]);

  // Função para carregar dados do dashboard usando o novo serviço
  const loadDashboardData = useCallback(async (accountId: string) => {
    if (!accountId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Load user data
      const userData = await safeEntityCall(() => User.me(), 'User', 'me');
      if (userData) {
        setUser(userData as UserWithAccount);
      }

      // Load monthly plan
      const plans = await safeEntityCall(() => 
        MonthlyPlan.filter({ 
          year: selectedYear, 
          month: selectedMonth + 1, // Converter 0-11 para 1-12 para o banco
          account_id: accountId
        }),
        'MonthlyPlan',
        'filter'
      );
      
      const plan = plans?.[0] || null;
      setCurrentPlan(plan);

      if (plan) {
        // Usar o CategoryDataService para carregar dados de categorias
        const filters: CategoryFilters = {
          account_id: accountId,
          monthly_plan_id: plan.id,
          include_subcategories: true
        };

        const response = await categoryDataService.getDashboardData(filters);
        
        if (response.success && response.data) {
          // Converter dados do serviço para o formato esperado pelo Dashboard
          const mappedCategories = response.data.categories.map(cat => ({
            id: cat.id,
            name: cat.name,
            type: cat.type,
            parent_id: cat.parent_id,
            planned_amount: cat.total_planned,
            spent_amount: cat.total_spent,
            planned_category_id: cat.planned_category_id
          }));
          
          setCategories(mappedCategories);
        } else {
          setError(response.error || 'Erro ao carregar dados de categorias');
        }

        // Load recent transactions (expenses and incomes) - only if plan exists
        const startDate = new Date(selectedYear, selectedMonth, 1);
        const endDate = new Date(selectedYear, selectedMonth + 1, 0);
        
        // Load ALL affected transactions for the current monthly plan
        const transactionsData = await safeEntityCall(() =>
          supabase
            .from('financial_records')
            .select(`
              *,
              category:financial_categories(*)
            `)
            .eq('account_id', accountId)
            .eq('monthly_plan_id', plan.id)
            .eq('status', 'affected')  // ← FILTRO CRÍTICO: apenas transações efetivadas
            .order('date', { ascending: false })
            .order('created_at', { ascending: false }),
          'FinancialRecords',
          'select'
        );

        if (transactionsData?.data) {
          // Separar despesas e receitas
          const expenseRecords = transactionsData.data
            .filter((record: any) => record.category?.type === 'expense')
            .map((record: any) => ({
              ...record,
              amount: parseFloat(record.amount || '0'),
              type: 'expense'
            }));

          const incomeRecords = transactionsData.data
            .filter((record: any) => record.category?.type === 'income')
            .map((record: any) => ({
              ...record,
              amount: parseFloat(record.amount || '0'),
              value: parseFloat(record.amount || '0'),
              type: 'income'
            }));
          
          // Para o componente RecentTransactions, usar apenas as 10 transações mais recentes
          const recentTransactions = transactionsData.data
            .slice(0, 10)
            .map((record: any) => ({
              ...record,
              amount: parseFloat(record.amount || '0'),
              type: record.category?.type
            }));
          
          // Para o componente RecentTransactions, usar apenas as 10 mais recentes
          setExpenses(recentTransactions.filter((t: any) => t.type === 'expense'));
          setIncomes(recentTransactions.filter((t: any) => t.type === 'income'));
        }
      } else {
        // Clear data if no plan exists
        setCategories([]);
        setExpenses([]);
        setIncomes([]);
      }
    } catch (error) {
      setError(NetworkErrorHandler.getErrorMessage(error as Error));
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // Usar o hook para recarregar dados automaticamente quando a conta mudar
  useAccountDataReloader(loadDashboardData, [selectedMonth, selectedYear]);

  // Fetch otimizado com error handling - removido o useEffect antigo
  useEffect(() => {
    if (currentAccountId) {
      loadDashboardData(currentAccountId);
    }
  }, [selectedMonth, selectedYear, currentAccountId, loadDashboardData]);

  // Error state display
  if (error) {
    return (
      <div className="p-4">
        <div className="mt-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Erro de Conexão
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <Button 
            onClick={() => loadDashboardData(currentAccountId)}
            className="bg-emerald-600 hover:bg-emerald-700"
            aria-label="Tentar Novamente"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  // Loading state - only show loading if we haven't finished loading AND we don't have a definitive answer about the plan
  if (isLoading) {
    return (
      <div className="p-4 flex justify-center items-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // No account selected state
  if (!user?.current_account_id) {
    return (
      <div className="p-4">
        <div className="mt-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Target className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Nenhuma conta selecionada
          </h2>
          <p className="text-gray-600 mb-6">
            Por favor, vá para as configurações para criar ou selecionar uma conta.
          </p>
          <Button 
            onClick={() => navigate(createPageUrl("Settings"))}
            className="bg-emerald-600 hover:bg-emerald-700"
            aria-label="Ir para Configurações"
          >
            Ir para Configurações
          </Button>
        </div>
      </div>
    );
  }

  // No plan for month state
  if (!isLoading && !currentPlan) {
    return (
      <div className="p-4">
        <MonthSelector />
        
        <div className="space-y-4 mt-6">
          <Card className="bg-white">
            <CardContent className="p-6 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum plano para {format(new Date(selectedYear, selectedMonth), 'MMMM yyyy', { locale: ptBR })}
              </h3>
              <p className="text-gray-600 mb-4">
                Crie um plano mensal para começar a organizar suas finanças e visualizar o dashboard
              </p>
              <Button 
                onClick={createNewPlan}
                className="bg-emerald-600 hover:bg-emerald-700"
                aria-label="Criar Plano Mensal"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Criar Plano Mensal
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <PageErrorBoundary pageName="Dashboard">
      <div className="p-4 space-y-6">
        {/* Cabeçalho da Página */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 text-sm">Visualize suas finanças de forma clara e organizada</p>
            </div>
          </div>
        </div>

        <CriticalComponentErrorBoundary componentName="Seletor de Mês">
          <MonthSelector />
        </CriticalComponentErrorBoundary>
        
        <CriticalComponentErrorBoundary componentName="Estatísticas do Dashboard">
          <DashboardStats plan={currentPlan} isLoading={false} />
        </CriticalComponentErrorBoundary>

        {/* Grid Layout para melhor organização */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de Despesas */}
          <div className="lg:col-span-1">
            <CriticalComponentErrorBoundary componentName="Gráfico de Despesas">
              <ExpensePieChart
                currentPlan={currentPlan}
                categories={categories}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                accountId={currentAccountId}
              />
            </CriticalComponentErrorBoundary>
          </div>

          {/* Transações Recentes */}
          <div className="lg:col-span-1">
            <CriticalComponentErrorBoundary componentName="Transações Recentes">
              <RecentTransactions
                expenses={expenses}
                incomes={incomes}
                categories={categories}
                isLoading={isLoading}
              />
            </CriticalComponentErrorBoundary>
          </div>
        </div>
        
        {/* Seção de Categorias removida conforme solicitação */}
      </div>
    </PageErrorBoundary>
  );
}

// Removido AlertComponent após migração para toasts

